import type {
  PwaRecoveryAction,
  PwaRecoveryCacheScope,
  PwaRecoveryEnvironment,
  PwaRecoveryManifest,
  PwaRecoveryRegistration,
  PwaRecoveryResult,
  PwaRecoveryStorage,
  PwaRecoveryWorker,
} from './types';

export const pwaRecoveryManifestPath = '/client-recovery.json';
export const pwaRecoveryStorageKey = 'forma-client-recovery-epoch';
export const pwaRecoveryReloadStorageKey = 'forma-client-recovery-reload-epoch';

const knownActions = new Set<PwaRecoveryAction>([
  'purge-owned-caches',
  'purge-media-cache',
  'purge-shell-cache',
  'update-service-worker',
  'reload',
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function parsePwaRecoveryManifest(
  value: unknown,
): PwaRecoveryManifest | null {
  if (!isObject(value) || value.schema !== 1) {
    return null;
  }

  if (typeof value.cacheEpoch !== 'string' || value.cacheEpoch.length === 0) {
    return null;
  }

  if (!Array.isArray(value.actions)) {
    return null;
  }

  const actions = value.actions.filter(
    (action): action is PwaRecoveryAction =>
      typeof action === 'string' &&
      knownActions.has(action as PwaRecoveryAction),
  );

  return {
    schema: 1,
    cacheEpoch: value.cacheEpoch,
    actions,
    message: typeof value.message === 'string' ? value.message : undefined,
  };
}

export function isFormaCacheForScope(
  cacheName: string,
  scope: PwaRecoveryCacheScope,
) {
  if (scope === 'media') {
    return cacheName.startsWith('forma-media-');
  }

  if (scope === 'shell') {
    return cacheName.startsWith('forma-shell-');
  }

  return (
    cacheName.startsWith('forma-media-') || cacheName.startsWith('forma-shell-')
  );
}

async function purgeBrowserCaches(
  caches: PwaRecoveryEnvironment['caches'],
  scope: PwaRecoveryCacheScope,
) {
  if (!caches) {
    return;
  }

  const cacheNames = await caches.keys();
  await Promise.all(
    cacheNames
      .filter((cacheName) => isFormaCacheForScope(cacheName, scope))
      .map((cacheName) => caches.delete(cacheName)),
  );
}

function readStorage(storage: PwaRecoveryStorage | undefined, key: string) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(
  storage: PwaRecoveryStorage | undefined,
  key: string,
  value: string,
) {
  try {
    storage?.setItem(key, value);
  } catch {
    // Recovery must not block normal startup when storage is unavailable.
  }
}

async function getRegistration(environment: PwaRecoveryEnvironment) {
  return environment.getServiceWorkerRegistration?.() ?? null;
}

async function messageWorker(
  environment: PwaRecoveryEnvironment,
  worker: PwaRecoveryWorker | null | undefined,
  message: unknown,
) {
  if (!worker || !environment.messageServiceWorker) {
    return;
  }

  await environment.messageServiceWorker(worker, message);
}

async function purgeServiceWorkerCaches(
  environment: PwaRecoveryEnvironment,
  registration: PwaRecoveryRegistration | null,
  scope: PwaRecoveryCacheScope,
) {
  const worker =
    registration?.active ??
    registration?.waiting ??
    registration?.installing ??
    null;

  await messageWorker(environment, worker, {
    type: 'PURGE_FORMA_CACHES',
    scope,
  });
}

async function applyRecoveryActions(
  manifest: PwaRecoveryManifest,
  environment: PwaRecoveryEnvironment,
) {
  const actionSet = new Set(manifest.actions);
  const registration = await getRegistration(environment);

  if (actionSet.has('purge-owned-caches')) {
    await purgeBrowserCaches(environment.caches, 'all');
    await purgeServiceWorkerCaches(environment, registration, 'all');
  } else {
    if (actionSet.has('purge-media-cache')) {
      await purgeBrowserCaches(environment.caches, 'media');
      await purgeServiceWorkerCaches(environment, registration, 'media');
    }

    if (actionSet.has('purge-shell-cache')) {
      await purgeBrowserCaches(environment.caches, 'shell');
      await purgeServiceWorkerCaches(environment, registration, 'shell');
    }
  }

  if (actionSet.has('update-service-worker')) {
    await registration?.update?.();
  }

  if (actionSet.has('reload')) {
    await messageWorker(environment, registration?.waiting, 'SKIP_WAITING');
  }
}

export async function applyPwaRecovery(
  environment: PwaRecoveryEnvironment,
): Promise<PwaRecoveryResult> {
  let manifest: PwaRecoveryManifest | null;

  try {
    const response = await environment.fetch(pwaRecoveryManifestPath, {
      cache: 'no-store',
    });

    if (!response.ok) {
      return { status: 'skipped', reason: 'manifest-unavailable' };
    }

    manifest = parsePwaRecoveryManifest(await response.json());
  } catch {
    return { status: 'skipped', reason: 'manifest-unavailable' };
  }

  if (!manifest) {
    return { status: 'skipped', reason: 'invalid-manifest' };
  }

  if (manifest.actions.length === 0) {
    return { status: 'skipped', reason: 'no-actions' };
  }

  if (
    readStorage(environment.localStorage, pwaRecoveryStorageKey) ===
    manifest.cacheEpoch
  ) {
    return { status: 'skipped', reason: 'already-applied' };
  }

  if (
    manifest.actions.includes('reload') &&
    readStorage(environment.sessionStorage, pwaRecoveryReloadStorageKey) ===
      manifest.cacheEpoch
  ) {
    return { status: 'skipped', reason: 'reload-already-attempted' };
  }

  await applyRecoveryActions(manifest, environment);
  writeStorage(
    environment.localStorage,
    pwaRecoveryStorageKey,
    manifest.cacheEpoch,
  );

  if (manifest.actions.includes('reload')) {
    writeStorage(
      environment.sessionStorage,
      pwaRecoveryReloadStorageKey,
      manifest.cacheEpoch,
    );
    environment.reload?.();
  }

  return {
    status: 'applied',
    epoch: manifest.cacheEpoch,
    actions: manifest.actions,
  };
}
